import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { Room } from "colyseus.js";
import { getStateCallbacks } from "colyseus.js";
import { useGame } from "@game/game-context";
import type {
  BattleActionErrorReason,
  BattleActionPayload,
  BattleEndEvent,
  BattleHandCard,
  BattleHandPayload,
  BattleLevelUp,
  BattleLogLine,
  BattlePhase,
  BattleRewardEvent,
  BattleSchema,
  BattleStrikeEvent,
  BattleTamerLevelUp,
  CombatantSchema,
} from "@game/types";

interface Props {
  roomId: string;
  mode: "pve" | "pvp_casual" | "pvp_ranked";
}

const ACTION_ERR_TEXT: Record<BattleActionErrorReason, string> = {
  phase_not_input: "It's not your turn.",
  unknown_skill: "Unknown skill.",
  skill_not_learned: "Your digimon doesn't know that skill.",
  not_enough_pp: "Not enough PP.",
  card_not_in_hand: "Card not in your hand.",
  unknown_card: "Unknown card.",
  not_enough_mana: "Not enough mana.",
  invalid_item: "That item can't be used in battle.",
  item_not_owned: "You don't have that item.",
  bench_not_found: "Can't swap to that digimon.",
  combatant_fainted: "Target is fainted.",
  invalid_target: "Invalid target.",
  internal: "Server error — try again.",
};

const wrapStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "#0b0d12",
  color: "#e6e8ee",
  fontFamily: "ui-sans-serif, system-ui, sans-serif",
  zIndex: 4500,
  display: "grid",
  gridTemplateRows: "auto 1fr auto",
  padding: "12px 16px",
};

export function BattleScreen({ roomId, mode }: Props) {
  const game = useGame();
  const [room, setRoom] = useState<Room<BattleSchema> | null>(null);
  const [state, setState] = useState<BattleSchema | null>(null);
  const [hand, setHand] = useState<BattleHandCard[]>([]);
  const [log, setLog] = useState<BattleLogLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reward, setReward] = useState<BattleRewardEvent | null>(null);
  const [levelUps, setLevelUps] = useState<BattleLevelUp[] | null>(null);
  const [tamerUp, setTamerUp] = useState<BattleTamerLevelUp | null>(null);
  const [endResult, setEndResult] = useState<BattleEndEvent | null>(null);
  const [strikes, setStrikes] = useState<BattleStrikeEvent | null>(null);
  const [, forceTick] = useState(0);

  // Connect
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const tok = game.client.auth.current()?.token;
        if (!tok) throw new Error("not authenticated");
        const r = await game.client.colyseus.joinById<BattleSchema>(
          roomId,
          { token: tok },
          {
            onLost: () => {
              if (!cancelled) setError("Disconnected from battle.");
            },
            onReconnect: (rr) => attach(rr as Room<BattleSchema>),
          },
        );
        if (cancelled) {
          r.leave();
          return;
        }
        attach(r);
        setRoom(r);
      } catch (err) {
        setError(`Could not join battle: ${(err as Error).message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const attach = (r: Room<BattleSchema>): void => {
    const $ = getStateCallbacks(r);
    setState({ ...r.state });
    r.onStateChange(() => setState({ ...r.state }));
    $(r.state).log.onAdd((l) => setLog((prev) => [...prev, l].slice(-200)));

    r.onMessage("hand", (h: BattleHandPayload) => setHand(h.cards ?? []));
    r.onMessage("action_error", (e: { reason: BattleActionErrorReason }) => {
      setError(ACTION_ERR_TEXT[e.reason] ?? `Error: ${e.reason}`);
    });
    r.onMessage("strike", (s: BattleStrikeEvent) => setStrikes(s));
    r.onMessage("reward", (rr: BattleRewardEvent) => setReward(rr));
    r.onMessage("level_ups", (ups: BattleLevelUp[]) => setLevelUps(ups));
    r.onMessage("tamer_level_up", (up: BattleTamerLevelUp) => setTamerUp(up));
    r.onMessage("end", (e: BattleEndEvent) => setEndResult(e));
  };

  // Turn deadline countdown
  useEffect(() => {
    if (!state?.turnDeadline) return;
    const id = window.setInterval(() => forceTick((t) => t + 1), 200);
    return () => window.clearInterval(id);
  }, [state?.turnDeadline]);

  const myActive: CombatantSchema | undefined = useMemo(() => {
    if (!state) return undefined;
    const id = state.player.activeCombatantId;
    return state.combatants.get(id) as CombatantSchema | undefined;
  }, [state]);

  const enemyActive: CombatantSchema | undefined = useMemo(() => {
    if (!state) return undefined;
    const id = state.enemy.activeCombatantId;
    return state.combatants.get(id) as CombatantSchema | undefined;
  }, [state]);

  const submit = (action: BattleActionPayload) => {
    setError(null);
    if (!room) return;
    if (state?.phase !== "input") {
      setError("Not your turn yet.");
      return;
    }
    // Client-side validation: skill must be in active.skills
    if (action.kind === "skill" && action.ref && myActive) {
      if (!myActive.skills.includes(action.ref)) {
        setError("Your digimon doesn't know that skill.");
        return;
      }
    }
    room.send("action", action);
  };

  const closeBattle = () => {
    room?.leave();
    game.setStage({ kind: "boot" }); // reload world
  };

  if (error && !state) {
    return (
      <div style={wrapStyle}>
        <BattleErrorScreen text={error} onClose={closeBattle} />
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      {/* Top: enemy side */}
      <BattleSide combatant={enemyActive} side="enemy" mode={mode} />

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, padding: "12px 0", overflow: "hidden" }}>
        {/* Center: combat log + phase */}
        <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <PhaseBanner phase={state?.phase} turn={state?.turn} />
            <DeadlineCounter deadline={state?.turnDeadline} />
          </div>
          <BattleLog log={log} />
        </div>

        {/* Right: side meta — hand count, mana, strikes */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SideMeta
            label="You"
            mana={state?.player.mana ?? 0}
            maxMana={state?.player.maxMana ?? 0}
            handCount={state?.player.handCount ?? 0}
            deckCount={state?.player.deckCount ?? 0}
            timeoutStrikes={state?.player.timeoutStrikes ?? 0}
            color="#60a5fa"
          />
          <SideMeta
            label="Enemy"
            mana={state?.enemy.mana ?? 0}
            maxMana={state?.enemy.maxMana ?? 0}
            handCount={state?.enemy.handCount ?? 0}
            deckCount={state?.enemy.deckCount ?? 0}
            timeoutStrikes={state?.enemy.timeoutStrikes ?? 0}
            color="#ef4444"
          />
          {strikes && strikes.side === "player" && strikes.strikes > 0 && (
            <div style={{ color: "#fbbf24", fontSize: 12 }}>⚠ Strike {strikes.strikes}/3</div>
          )}
        </div>
      </div>

      {/* Bottom: my side combatant + actions + hand */}
      <div>
        <BattleSide combatant={myActive} side="player" mode={mode} />
        {myActive && state && (
          <BattleActions
            combatant={myActive}
            hand={hand}
            bench={state.player.bench}
            mana={state.player.mana}
            phase={state.phase}
            mode={mode}
            onAction={submit}
          />
        )}
        {error && (
          <div style={{ color: "#ef4444", marginTop: 6, fontSize: 12 }}>{error}</div>
        )}
      </div>

      {/* Modals: reward / levelup / end */}
      {(reward || levelUps || tamerUp || endResult) && (
        <BattleEndOverlay
          reward={reward}
          levelUps={levelUps}
          tamerUp={tamerUp}
          end={endResult}
          onClose={closeBattle}
        />
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function BattleSide({ combatant, side, mode }: { combatant: CombatantSchema | undefined; side: "player" | "enemy"; mode: string }) {
  if (!combatant) {
    return (
      <div style={{ opacity: 0.5, padding: 12 }}>
        {side === "player" ? "Your party is loading…" : "Awaiting opponent…"} ({mode})
      </div>
    );
  }
  const hpFrac = combatant.maxHp > 0 ? combatant.hp / combatant.maxHp : 0;
  const color = side === "player" ? "#60a5fa" : "#ef4444";
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${color}55`, borderRadius: 8, padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ color, fontWeight: 600 }}>
          {combatant.speciesName} <span style={{ opacity: 0.65 }}>Lv {combatant.level}</span>
        </span>
        <span style={{ fontSize: 11, opacity: 0.7 }}>
          {combatant.element.toUpperCase()}
        </span>
      </div>
      <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", fontSize: 12, opacity: 0.85 }}>
        <span>
          HP {Math.round(combatant.hp)} / {Math.round(combatant.maxHp)}
        </span>
        {combatant.fainted && <span style={{ color: "#ef4444" }}>FAINTED</span>}
      </div>
      <div style={{ height: 6, marginTop: 4, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}>
        <div
          style={{
            width: `${Math.round(hpFrac * 100)}%`,
            height: "100%",
            background: hpFrac > 0.5 ? "#4ade80" : hpFrac > 0.25 ? "#facc15" : "#ef4444",
            transition: "width 200ms linear",
          }}
        />
      </div>
      {combatant.statuses.length > 0 && (
        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {combatant.statuses.map((s, i) => (
            <span
              key={`${s.name}-${i}`}
              style={{
                fontSize: 10,
                background: "rgba(168,85,247,0.18)",
                color: "#c4b5fd",
                padding: "1px 6px",
                borderRadius: 3,
              }}
              title={`${s.name} ×${s.potency} (${s.remaining}t)`}
            >
              {s.name} {s.remaining}t
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function PhaseBanner({ phase, turn }: { phase?: BattlePhase | undefined; turn?: number | undefined }) {
  if (!phase) return null;
  const color =
    phase === "input"
      ? "#86efac"
      : phase === "resolve"
        ? "#facc15"
        : phase === "victory"
          ? "#60a5fa"
          : phase === "defeat"
            ? "#ef4444"
            : "#9ca3af";
  return (
    <span style={{ color, fontSize: 13, fontWeight: 600 }}>
      {phase.toUpperCase()}
      {turn !== undefined ? ` · turn ${turn}` : ""}
    </span>
  );
}

function DeadlineCounter({ deadline }: { deadline?: number | undefined }) {
  if (!deadline) return null;
  const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  const color = remaining < 5 ? "#ef4444" : remaining < 15 ? "#facc15" : "#9ca3af";
  return <span style={{ color, fontSize: 12 }}>⏱ {remaining}s</span>;
}

function BattleLog({ log }: { log: BattleLogLine[] }) {
  return (
    <div
      style={{
        flex: 1,
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 6,
        padding: 8,
        fontSize: 12,
        overflowY: "auto",
        fontFamily: "ui-monospace, monospace",
      }}
    >
      {log.length === 0 ? (
        <span style={{ opacity: 0.5 }}>(battle log will appear here)</span>
      ) : (
        log.map((l, i) => (
          <div key={`${l.ts}-${i}`} style={{ opacity: 0.85, marginBottom: 1 }}>
            {l.text}
          </div>
        ))
      )}
    </div>
  );
}

function SideMeta({
  label, mana, maxMana, handCount, deckCount, timeoutStrikes, color,
}: { label: string; mana: number; maxMana: number; handCount: number; deckCount: number; timeoutStrikes: number; color: string }) {
  const f = maxMana > 0 ? mana / maxMana : 0;
  return (
    <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${color}33`, borderRadius: 6, padding: 8, fontSize: 12 }}>
      <div style={{ color, fontWeight: 600 }}>{label}</div>
      <div style={{ marginTop: 4, opacity: 0.85 }}>
        Mana {mana} / {maxMana}
      </div>
      <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden", marginTop: 2 }}>
        <div style={{ width: `${Math.round(f * 100)}%`, height: "100%", background: color }} />
      </div>
      <div style={{ marginTop: 4, opacity: 0.65, fontSize: 11 }}>
        Hand {handCount} · Deck {deckCount}
        {timeoutStrikes > 0 && ` · Strikes ${timeoutStrikes}/3`}
      </div>
    </div>
  );
}

// =============================================================================
// Action panel — skills / cards / items / swap / pass / flee
// =============================================================================

function BattleActions({
  combatant, hand, bench, mana, phase, mode, onAction,
}: {
  combatant: CombatantSchema;
  hand: BattleHandCard[];
  bench: string[];
  mana: number;
  phase: BattlePhase;
  mode: string;
  onAction: (a: BattleActionPayload) => void;
}) {
  const disabled = phase !== "input";
  const canFlee = mode === "pve";
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {combatant.skills.map((s) => {
          const pp = (combatant.pp.get?.(s) as number | undefined) ?? 0;
          return (
            <button
              key={s}
              type="button"
              disabled={disabled || pp <= 0}
              onClick={() => onAction({ kind: "skill", ref: s })}
              style={btnStyle(false, disabled || pp <= 0)}
              title={`${s} (PP ${pp})`}
            >
              ⚔ {s}
              <span style={{ opacity: 0.6, fontSize: 10, marginLeft: 4 }}>{pp}pp</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {hand.map((c) => {
          const cantPay = c.cost > mana;
          return (
            <button
              key={c.cardInstanceId}
              type="button"
              disabled={disabled || cantPay}
              onClick={() => onAction({ kind: "card", ref: c.cardInstanceId })}
              style={btnStyle(true, disabled || cantPay)}
              title={`${c.name} — ${c.cost} mana — ${c.target}`}
            >
              🃏 {c.name}
              <span style={{ opacity: 0.7, fontSize: 10, marginLeft: 4 }}>{c.cost}m</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {bench.length > 0 && (
          <select
            disabled={disabled}
            onChange={(e) => {
              const v = e.target.value;
              if (v) onAction({ kind: "swap", ref: v });
              e.currentTarget.selectedIndex = 0;
            }}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#e6e8ee",
              borderRadius: 4,
              padding: "4px 8px",
              fontSize: 12,
            }}
          >
            <option value="">⇄ Swap…</option>
            {bench.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAction({ kind: "pass" })}
          style={btnStyle(false, disabled)}
        >
          Pass
        </button>
        {canFlee && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onAction({ kind: "flee" })}
            style={btnStyle(false, disabled, "#ef4444")}
          >
            Flee
          </button>
        )}
      </div>
    </div>
  );
}

function btnStyle(card: boolean, disabled: boolean, accent?: string): CSSProperties {
  return {
    background: card ? "rgba(168,85,247,0.18)" : "rgba(96,165,250,0.18)",
    border: `1px solid ${accent ?? (card ? "rgba(168,85,247,0.6)" : "rgba(96,165,250,0.6)")}`,
    color: "#e6e8ee",
    padding: "4px 10px",
    borderRadius: 4,
    fontSize: 12,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
  };
}

// =============================================================================
// End-of-battle overlay
// =============================================================================

function BattleEndOverlay({
  reward, levelUps, tamerUp, end, onClose,
}: {
  reward: BattleRewardEvent | null;
  levelUps: BattleLevelUp[] | null;
  tamerUp: BattleTamerLevelUp | null;
  end: BattleEndEvent | null;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 5500,
      }}
    >
      <div
        style={{
          width: 420,
          background: "#0f1218",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 12,
          padding: 24,
          color: "#e6e8ee",
          fontFamily: "ui-sans-serif, system-ui, sans-serif",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 22, color: end?.victorySide === "player" ? "#86efac" : "#ef4444" }}>
          {end?.victorySide === "player" ? "Victory!" : end ? "Defeated" : "Battle"}
        </h2>
        {reward && (
          <div style={{ marginTop: 14, fontSize: 13 }}>
            <div>Bits: <strong>{reward.bits}</strong></div>
            <div>XP: <strong>{reward.xp}</strong></div>
            {reward.loot.length > 0 && (
              <div style={{ marginTop: 6 }}>
                Loot:
                <ul style={{ margin: "4px 0 0 16px", padding: 0, fontSize: 12 }}>
                  {reward.loot.map((l, i) => (
                    <li key={i}>
                      {l.kind} · {l.id} × {l.qty}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {levelUps && levelUps.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            {levelUps.map((u) => (
              <div key={u.digimonId}>
                <strong style={{ color: "#fbbf24" }}>Lv {u.from} → {u.to}</strong>
                {u.skills.length > 0 && <span style={{ opacity: 0.65 }}> · learned {u.skills.join(", ")}</span>}
              </div>
            ))}
          </div>
        )}
        {tamerUp && (
          <div style={{ marginTop: 8, fontSize: 13, color: "#fbbf24" }}>
            ↑ Tamer Lv {tamerUp.oldLevel} → {tamerUp.newLevel}
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 20,
            width: "100%",
            background: "linear-gradient(135deg, #4cb1ff, #a855f7)",
            color: "#fff",
            border: "none",
            padding: "10px",
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function BattleErrorScreen({ text, onClose }: { text: string; onClose: () => void }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <div style={{ color: "#ef4444", fontSize: 16 }}>{text}</div>
      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: 16,
          background: "#2563eb",
          color: "#fff",
          border: "none",
          padding: "6px 14px",
          borderRadius: 6,
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        Back
      </button>
    </div>
  );
}
